/**
 * ErrorBoundary - Catches React errors and unhandled promise rejections.
 *
 * Supports a friendly fallback UI with a Retry button. When the optional
 * `lastSuccessAt` prop is supplied, the last successful load time is surfaced
 * so the user knows how stale the visible data is. Consumers can also pass a
 * custom `onRetry` handler; the default resets internal error state so the
 * wrapped subtree re-renders.
 */
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional timestamp of the last successful load to show to the user. */
  lastSuccessAt?: Date | string | null;
  /** Optional custom retry handler. Defaults to clearing error state. */
  onRetry?: () => void;
  /** Override the user-facing title. */
  title?: string;
  /** Override the user-facing body message. */
  message?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  /**
   * True while a ChunkLoadError-driven auto-reload is in flight. We render
   * null in this case so the user doesn't see a flash of the error UI
   * between `getDerivedStateFromError` and the browser navigation.
   */
  isReloadingForChunk: boolean;
}

const formatLastSuccess = (value: Date | string | null | undefined): string | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

// A redeploy invalidates the lazy-loaded chunk hashes referenced by the
// currently-loaded main.js. The next import() rejects with ChunkLoadError.
// index.html is served no-cache, so a full reload picks up the fresh main.js
// and its matching chunks. The sessionStorage guard prevents a reload loop if
// the deploy is actually broken (after one auto-reload attempt within the
// window, we surface the regular error UI).
const CHUNK_RELOAD_KEY = 'fnf:chunkReloadAt';
const CHUNK_RELOAD_BACKOFF_MS = 60_000;

const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;
  const e = error as { name?: unknown; message?: unknown; code?: unknown };
  if (e.name === 'ChunkLoadError') return true;
  if (e.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  const msg = typeof e.message === 'string' ? e.message : '';
  return /Loading (?:CSS )?chunk [\w-]+ failed/i.test(msg);
};

const tryAutoReloadForChunkError = (): boolean => {
  try {
    const last = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? '0', 10);
    if (Number.isFinite(last) && Date.now() - last < CHUNK_RELOAD_BACKOFF_MS) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage can throw in private mode / quota-exceeded. Reload anyway
    // — worst case is a loop the user breaks with hard-refresh, which is
    // strictly better than a permanent blank section after a deploy.
  }
  window.location.reload();
  return true;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private rejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isReloadingForChunk: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Mark chunk errors as "reloading" so render() returns null and the user
    // doesn't see the regular error UI flash before componentDidCatch fires
    // the actual reload.
    if (isChunkLoadError(error)) {
      return { hasError: true, isReloadingForChunk: true };
    }
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    if (isChunkLoadError(error)) {
      if (tryAutoReloadForChunkError()) {
        return;
      }
      // Rate-limited (we already reloaded in this session) — let the error
      // UI render so the user can manually retry or hard-refresh.
      this.setState({ error, errorInfo, isReloadingForChunk: false });
      return;
    }
    this.setState({ error, errorInfo });
  }

  componentDidMount(): void {
    this.rejectionHandler = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      if (isChunkLoadError(event.reason)) {
        if (tryAutoReloadForChunkError()) {
          this.setState({ hasError: true, isReloadingForChunk: true });
          return;
        }
        // Rate-limited — fall through to surface the error UI.
      }
      this.setState({
        hasError: true,
        error:
          event.reason instanceof Error
            ? event.reason
            : new Error('Unhandled promise rejection'),
        errorInfo: { componentStack: 'Promise rejection' } as ErrorInfo,
        isReloadingForChunk: false,
      });
    };
    window.addEventListener('unhandledrejection', this.rejectionHandler);
  }

  componentWillUnmount(): void {
    if (this.rejectionHandler) {
      window.removeEventListener('unhandledrejection', this.rejectionHandler);
    }
  }

  private handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isReloadingForChunk: false,
    });
    if (this.props.onRetry) {
      try {
        this.props.onRetry();
      } catch (err) {
        console.error('ErrorBoundary onRetry handler failed:', err);
      }
    }
  };

  render(): ReactNode {
    if (this.state.isReloadingForChunk) {
      // Page is about to reload — render nothing so the error UI doesn't flash.
      return null;
    }
    if (!this.state.hasError) {
      return this.props.children;
    }

    const lastSuccessLabel = formatLastSuccess(this.props.lastSuccessAt);
    const title = this.props.title ?? 'Something went wrong';
    const message =
      this.props.message ??
      'We were unable to load this section. You can retry or reload the page.';

    return (
      <div
        style={{
          padding: '2rem',
          maxWidth: '800px',
          margin: '2rem auto',
          backgroundColor: '#fff',
          border: '2px solid #e74c3c',
          borderRadius: '8px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <h2 style={{ color: '#e74c3c', marginTop: 0 }}>{title}</h2>
        <p style={{ marginBottom: '0.5rem' }}>{message}</p>
        {lastSuccessLabel && (
          <p style={{ marginTop: 0, fontSize: '0.875rem', color: '#555' }}>
            Last successful load: {lastSuccessLabel}
          </p>
        )}

        <details
          style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
          }}
        >
          <summary
            style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}
          >
            Error details (click to expand)
          </summary>
          <pre
            style={{
              overflow: 'auto',
              fontSize: '12px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </pre>
        </details>

        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={this.handleRetry}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            Retry
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#ecf0f1',
              color: '#2c3e50',
              border: '1px solid #bdc3c7',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
